<template>
  <div
    v-if="!!dataToDisplay"
    class="i-plan-details"
  >
    <div class="top">
      <div class="header">{{ dataToDisplay.header }}</div>
      <div class="sub">{{ dataToDisplay.subText }}</div>
    </div>
    <iBaseButton
      v-bind="MAIN_BUTTON_STYLE"
      :text="dataToDisplay.actionText"
      :icon-url="dataToDisplay.icon"
      icon-width="22px"
      @clicked="dataToDisplay.action()"
    />
    <div class="perks">
      <div
        v-for="perk in dataToDisplay.perks"
        :key="perk"
        class="perk"
      >
        <img
          :src="iconGreenCheckmark"
          class="icon"
        />
        {{ perk }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { bluePurpleGradient, white } from "@/styles/colors"

import getPaymentAction from "@/composables/getPaymentAction"
import { GlobalVariables } from "@/helpers/globalVariables"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconCalendarRepeatWhite from "@/assets/icons/icon-calendar-repeat-white.svg"
import iconFilePlusWhite from "@/assets/icons/icon-file-plus-white.svg"
import iconGreenCheckmark from "@/assets/icons/icon-green-checkmark.svg"

// TYPES
import StripePrice from "@/shared/enums/StripePrice"
import { UserFlow } from "@/services/gtmService"

// STORES AND ROUTES
const userStore = useUserStore()
const router = useRouter()

// CONSTANTS
const MAIN_BUTTON_STYLE = {
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "100%",
  padding: "10px 16px",
  size: "small"
}

// COMPUTED
const subscriberResumeData = computed(() => {
  return {
    header: "Resume Package",
    subText: "Optimize your job search toolkit",
    perks: ["Professionally Written Resume", "Resume ATS Optimization"],
    actionText: "Buy Resume Package",
    icon: iconFilePlusWhite,
    action: () => firePaymentAction(StripePrice.RESUME_COACHING_ADDON, UserFlow.RESUME)
  }
})
const nonSubscriberData = computed(() => {
  return {
    header: "Unlock Your Potential",
    subText: `${userStore.hasPurchasedExecutiveSubscription ? "Executive" : "Accelerated"} plan to achieve your goals`,
    perks: [
      `As low as $${userStore.hasPurchasedExecutiveSubscription ? "549.00" : "179.90"}/month`,
      `Up to ${userStore.hasPurchasedExecutiveSubscription ? "4" : "2"} hours of 1:1 coaching per month`,
      "Weekly live group coaching"
    ],
    actionText: userStore.isCoachingOnce ? "Subscribe" : "Resubscribe",
    icon: iconCalendarRepeatWhite,
    action: goToSelectPlan
  }
})
const dataToDisplay = computed(() =>
  userStore.isCoachingCancelled || (userStore.isCoachingOnce && !userStore.hasPurchasedExecutiveDiscovery)
    ? nonSubscriberData.value
    : !userStore.userData.hasResumePackage
      ? subscriberResumeData.value
      : null
)

// FUNCTION
const firePaymentAction = async (stripePrice: StripePrice, userFlow: UserFlow) => {
  const stripePrices = [stripePrice]
  const paymentAction = getPaymentAction(stripePrices, userFlow)

  try {
    await paymentAction()
  } catch (error) {
    console.error(`Failed to create checkout session for ${stripePrice}`, error)
  }
}

const goToSelectPlan = () => {
  router.push({
    name: userStore.isExecutiveCoachingPotential
      ? GlobalVariables.urls.coaching.children.choosePlan.routeName
      : GlobalVariables.urls.coaching.children.selectPlan.routeName
  })
}
</script>

<style lang="scss" scoped>
.i-plan-details {
  @include flex(column, start, stretch, $gap: 20px);
  @include border-radius-md;

  border: 1px solid $zumthor100;
  width: 100%;
  padding: 12px 12px 16px;
  font-weight: 400;
  background-color: $black50;

  .top {
    @include flex(column, start, stretch, $gap: 2px);
    @include text-xs;

    border-bottom: 1px solid $zumthor100;
    padding-bottom: 20px;
    color: $black600;
    font-weight: 300;
    font-size: 13px;

    .header {
      @include text-lg;

      color: $pickle-bluewood950;
      font-weight: 600;
    }
  }

  .perks {
    @include flex(column, start, stretch, $gap: 10px);
    @include text-sm;

    .perk {
      @include flex(row, start, center, $gap: 5px);

      .icon {
        height: 20px;
        width: 20px;
      }
    }
  }
}
</style>
